import { useState, useEffect, useCallback, useRef } from "react";
import { Bell, X, UserPlus, Clock, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axCfg = { withCredentials: true };

// Sonido de notificación (usando Web Audio API)
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800;
    oscillator.type = "sine";
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
    
    // Segundo tono
    setTimeout(() => {
      const osc2 = audioContext.createOscillator();
      const gain2 = audioContext.createGain();
      osc2.connect(gain2);
      gain2.connect(audioContext.destination);
      osc2.frequency.value = 1000;
      osc2.type = "sine";
      gain2.gain.setValueAtTime(0.3, audioContext.currentTime);
      gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      osc2.start(audioContext.currentTime);
      osc2.stop(audioContext.currentTime + 0.3);
    }, 150);
  } catch (e) {
    console.log("Audio not supported");
  }
};

export default function NotificationBell({ onNavigateToProspectos }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [lastCheckTime, setLastCheckTime] = useState(() => {
    // Recuperar última hora de verificación del localStorage
    const saved = localStorage.getItem("notification_last_check");
    return saved ? new Date(saved) : new Date();
  });
  const previousProspectsRef = useRef([]);
  const isFirstLoad = useRef(true);

  const fetchNewProspects = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/diagnostic-requests`, axCfg);
      
      // Filtrar solo los de landing page o sin source definido
      const landingProspects = data.filter(p => 
        p.created_by_source === "landing_page" || !p.created_by_source
      );
      
      // Prospectos sin atender (sin cotización y no asignados)
      const unattendedProspects = landingProspects.filter(p => 
        !p.has_quotation && !p.assigned_to_user_id
      );
      
      // En la primera carga, crear notificaciones para prospectos no atendidos
      if (isFirstLoad.current && unattendedProspects.length > 0) {
        const initialNotifications = unattendedProspects.map(p => ({
          id: p.id,
          type: "pending_prospect",
          title: "Prospecto sin atender",
          message: `${p.company_name || p.full_name} - ${p.service_type}`,
          timestamp: new Date(p.created_at),
          read: false,
          data: p
        }));
        setNotifications(initialNotifications.slice(0, 10));
        setUnreadCount(unattendedProspects.length);
      }
      
      // Detectar nuevos prospectos comparando con la lista anterior (para notificaciones en tiempo real)
      if (!isFirstLoad.current && previousProspectsRef.current.length > 0) {
        const previousIds = new Set(previousProspectsRef.current.map(p => p.id));
        const newProspects = landingProspects.filter(p => !previousIds.has(p.id));
        
        if (newProspects.length > 0) {
          // Hay nuevos prospectos - reproducir sonido y mostrar notificación
          playNotificationSound();
          
          // Agregar notificaciones
          const newNotifications = newProspects.map(p => ({
            id: p.id,
            type: "new_prospect",
            title: "🔔 Nuevo Prospecto",
            message: `${p.company_name || p.full_name} solicita diagnóstico`,
            timestamp: new Date(p.created_at),
            read: false,
            data: p
          }));
          
          setNotifications(prev => [...newNotifications, ...prev].slice(0, 20));
          setUnreadCount(prev => prev + newProspects.length);
          
          // Mostrar notificación del navegador si está permitido
          if (Notification.permission === "granted") {
            newProspects.forEach(p => {
              new Notification("🔔 Nuevo Prospecto - PETROCONTROL", {
                body: `${p.company_name || p.full_name} solicita diagnóstico gratuito`,
                icon: "/favicon.ico",
                tag: p.id
              });
            });
          }
        }
      }
      
      previousProspectsRef.current = landingProspects;
      isFirstLoad.current = false;
      
    } catch (e) {
      console.error("Error fetching prospects:", e);
    }
  }, []);

  // Solicitar permiso para notificaciones del navegador
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  // Polling cada 10 segundos
  useEffect(() => {
    fetchNewProspects();
    const interval = setInterval(fetchNewProspects, 10000);
    return () => clearInterval(interval);
  }, [fetchNewProspects]);

  // Guardar última hora de verificación
  useEffect(() => {
    localStorage.setItem("notification_last_check", lastCheckTime.toISOString());
  }, [lastCheckTime]);

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    setLastCheckTime(new Date());
  };

  const handleNotificationClick = (notification) => {
    // Marcar como leída
    setNotifications(prev => 
      prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
    
    // Navegar a prospectos
    if (onNavigateToProspectos) {
      onNavigateToProspectos();
    }
    setIsOpen(false);
  };

  const removeNotification = (e, notificationId) => {
    e.stopPropagation();
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const formatTime = (date) => {
    const now = new Date();
    const diff = now - new Date(date);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return "Ahora";
    if (minutes < 60) return `Hace ${minutes} min`;
    if (hours < 24) return `Hace ${hours}h`;
    return `Hace ${days}d`;
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm"
          className="relative text-white/60 hover:text-white"
          data-testid="notification-bell"
        >
          <Bell className={`h-5 w-5 ${unreadCount > 0 ? 'animate-pulse' : ''}`} />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-bold animate-bounce">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-3 border-b bg-[#1a5c4a] text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              <span className="font-semibold">Notificaciones</span>
            </div>
            {unreadCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-white/80 hover:text-white text-xs h-auto py-1"
                onClick={markAllAsRead}
              >
                Marcar leídas
              </Button>
            )}
          </div>
        </div>
        
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <Bell className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No hay notificaciones</p>
              <p className="text-xs mt-1">Los nuevos prospectos aparecerán aquí</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                    !notification.read ? 'bg-amber-50' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      !notification.read ? 'bg-amber-500' : 'bg-gray-200'
                    }`}>
                      <UserPlus className={`h-4 w-4 ${!notification.read ? 'text-white' : 'text-gray-500'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm ${!notification.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                          {notification.title}
                        </p>
                        <button
                          onClick={(e) => removeNotification(e, notification.id)}
                          className="text-gray-400 hover:text-gray-600 p-1"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-600 truncate">{notification.message}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Clock className="h-3 w-3 text-gray-400" />
                        <span className="text-xs text-gray-400">{formatTime(notification.timestamp)}</span>
                        {!notification.read && (
                          <Badge className="bg-amber-100 text-amber-700 text-xs px-1.5 py-0">
                            Nuevo
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {notifications.length > 0 && (
          <div className="p-2 border-t bg-gray-50">
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full text-[#1a5c4a] hover:bg-[#1a5c4a]/10 text-sm"
              onClick={() => {
                if (onNavigateToProspectos) onNavigateToProspectos();
                setIsOpen(false);
              }}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Ver todos los prospectos
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
